/**
 * ENVOYER UNE SÉLECTION DU CATALOGUE AUX ARTICLES RAPIDES — la fenêtre de destination de la maquette (`qkSend`),
 * ouverte par « Articles rapides » dans la barre de sélection.
 *
 * ⚠️ ELLE FERME LA BOUCLE. Sans elle, garnir une disposition n'était possible que depuis l'écran des
 * articles rapides, alors que le geste naturel est de choisir ses articles dans le catalogue — où on
 * les cherche, les filtre et les compare — puis de les envoyer.
 *
 * ⚠️ LA FENÊTRE EST CELLE DU DESSIN, PAS LE CADRE DE LA CHARTE (décision utilisateur : « la modale doit suivre la
 * maquette ») : une carte de 460 px sans liseré ni tuile, la tête sans croix, le pied grisé avec « Ajouter et
 * organiser » en encre. Le clavier (Échap, focus, retour du focus) reste au crochet partagé, comme les fenêtres du studio.
 *
 * ⚠️ « +12 », « 3 DÉJÀ DEDANS » ET « 8 ARTICLES À L'ACCUEIL » VIENNENT DU SERVEUR. La maquette les calcule en mémoire
 * parce qu'elle tient tout le catalogue ; ici le navigateur ne connaît pas le contenu des dispositions. Les déduire
 * donnerait un nombre plausible et faux — or c'est CE nombre qui fait choisir la destination (§23.0).
 *
 * ⚠️ LA DISPOSITION PAR DÉFAUT EN TÊTE, ET SES CAISSES NON AFFECTÉES NOMMÉES À PART (« R01 · R02 + non affectées : R04 ») :
 * c'est elle qui sert toute caisse qu'aucune autre ne prend, et les mêler à ses caisses attribuées laissait croire
 * qu'on les lui avait données.
 *
 * ⚠️ UN MAGASIN A TOUJOURS SA DISPOSITION PAR DÉFAUT (décision utilisateur du 2026-11-03, « ça devient vrai ») : elle
 * naît vide au premier besoin, ici comme à l'ouverture du studio, par la même écriture idempotente — jamais par un
 * script sur les bases.
 *
 * ⚠️ L'ENVOI VA AU BROUILLON DU STUDIO, jamais aux caisses : la publication réécrit la liste des caisses depuis
 * l'arbre, donc un envoi écrit à côté disparaîtrait à la publication suivante. Le gérant retrouve ses articles
 * « Non publié » à l'accueil de la disposition choisie, et l'annonce le dit.
 *
 * ⚠️ LE CATALOGUE SE SÉLECTIONNE PAR MODÈLE, LA CAISSE AFFICHE DES DÉCLINAISONS. Quand les deux comptes diffèrent, la tête
 * le dit (« 12 articles sélectionnés (47 déclinaisons) ») : un modèle en six tailles et quatre coloris vaut vingt-quatre
 * tuiles, et personne ne veut le découvrir après coup.
 */
import { useEffect, useId, useState, type CSSProperties } from 'react';
import { ModalBackdrop, showToast, Tooltip, Trans, useOverlayPanel } from '@orgazone/ui-web';
import { api, type QuickLayout } from '../api';
import { t } from '../i18n';
import { codeCaisse } from '../caisse';
import { defaultLayoutAudience, firstLayoutName } from './quick-studio-model';

type Destination = QuickLayout & { free: string[]; home: number; already: number };

/**
 * QUI AFFICHE LA DISPOSITION, puis ce qu'elle porte déjà — la sous-ligne de la maquette (`qkSendRows.sub`). Les comptes
 * nuls se taisent : « 0 article à l'accueil » n'apprend rien à qui choisit une destination.
 */
function destinationSubline(l: Destination): string {
  const who = l.isDefault
    ? defaultLayoutAudience(l.registers, l.free)
    : (l.registers.map(codeCaisse).join(' · ') || t('aucune caisse — affichée nulle part'));
  return [
    who,
    l.home > 0 ? t('{n} article à l’accueil', { n: l.home }) : null,
    l.already > 0 ? t('{n} déjà dedans', { n: l.already }) : null,
  ].filter(Boolean).join(' · ');
}

/** Les boutons du pied, aux mesures du dessin — un état inerte qui en a l'air. */
const FOOT_BUTTON: CSSProperties = { height: 36, padding: '0 15px', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: 'pointer' };
const inert = (off: boolean): CSSProperties => (off ? { opacity: 0.55, cursor: 'not-allowed' } : {});

export function EnvoiArticlesRapides({ modelKeys, onClose, onOrganiser }: {
  /** Les clés de MODÈLE sélectionnées au catalogue — le serveur les expanse en déclinaisons. */
  modelKeys: string[];
  onClose: () => void;
  /** « Ajouter et organiser » : on part sur l'écran des articles rapides, disposition ouverte. */
  onOrganiser: (layoutId: string) => void;
}) {
  const [apercu, setApercu] = useState<{ models: number; variants: number; rows: Destination[] } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [target, setCible] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const { panelRef, close } = useOverlayPanel({ open: true, onClose, blockClose: () => writing });
  const titleId = useId();

  useEffect(() => {
    let vivant = true;
    const read = () => api.quickDestinations(modelKeys);
    read()
      .then((r) => (r.rows.length > 0 ? r : api.ensureFirstQuickLayout(firstLayoutName()).then(read)))
      .then((r) => {
        if (!vivant) return;
        setApercu({ ...r, rows: [...r.rows].sort((a, b) => Number(b.isDefault) - Number(a.isDefault)) });
        // La destination proposée est la disposition PAR DÉFAUT (relevé) : c'est celle que prend une
        // caisse non rattachée, donc celle qui touche le plus de monde.
        setCible((r.rows.find((l) => l.isDefault) ?? r.rows[0])?.id ?? null);
      })
      .catch((e) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKeys]);

  const envoyer = async (organiser: boolean) => {
    if (!target || writing) return;
    setWriting(true);
    try {
      const r = await api.addModelsToStudio(target, modelKeys);
      const nom = apercu?.rows.find((l) => l.id === target)?.name ?? t('la disposition');
      // ⚠️ L'ANNONCE DU DESSIN, plus ce qu'il tait : rien ne descend aux caisses avant la publication du studio.
      showToast(
        r.added > 0
          ? t('{n} article envoyé vers « {nom} » — à l’écran d’accueil, à publier depuis les articles rapides.', { n: r.added, nom })
          : t('Déjà tous dans « {nom} »', { nom }),
        r.added > 0 ? 'ok' : 'info',
      );
      onClose();
      if (organiser) onOrganiser(target);
    } catch (e) {
      // ⚠️ MONTRÉ, JAMAIS AVALÉ : un refus nommé (trop de déclinaisons, aucun gencode) est la réponse
      // utile — et la fenêtre RESTE ouverte, la sélection intacte, pour qu'on puisse en tenir compte.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally { setWriting(false); }
  };

  const off = !target || writing;
  const strong = (x: string) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>;

  return (
    <ModalBackdrop onBackdrop={close}>
      <div
        ref={panelRef} role="dialog" aria-modal aria-labelledby={titleId} tabIndex={-1}
        style={{
          position: 'relative', width: 460, maxWidth: '100%', maxHeight: '82vh', display: 'flex', flexDirection: 'column',
          background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: '0 28px 70px -18px rgba(12,14,16,.45)', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '18px 20px 4px' }}>
          <div id={titleId} style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ajouter aux articles rapides')}</div>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 3 }}>
            {apercu === null
              ? t('Lecture des dispositions…')
              : apercu.variants !== apercu.models
                ? <Trans k="<1>{n}</1> article sélectionné ({declinaisons}) — choisissez la disposition de destination." vars={{ n: apercu.models, declinaisons: t('{n} déclinaison', { n: apercu.variants }) }} parts={[strong]} />
                : <Trans k="<1>{n}</1> article sélectionné — choisissez la disposition de destination." vars={{ n: apercu.models }} parts={[strong]} />}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {apercu?.rows.length === 0 && (
            <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              {t('Ce magasin n’a aucune disposition d’articles rapides. Créez-en une depuis l’écran « Articles rapides » — la première créée devient celle que prend toute caisse.')}
            </p>
          )}
          {apercu?.rows.map((l) => {
            const on = l.id === target;
            const neuves = apercu.variants - l.already;
            return (
              <button
                key={l.id} type="button" onClick={() => setCible(l.id)} aria-pressed={on}
                style={{
                  display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', textAlign: 'left',
                  border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                  borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-100)' : 'var(--paper)', cursor: 'pointer',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 17, height: 17, borderRadius: 'var(--r-pill)', boxSizing: 'border-box', flexShrink: 0,
                    border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                    background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {on && <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{l.name}</span>
                    {l.isDefault && (
                      <Tooltip content={t('Créée dans chaque magasin : elle s’affiche sur toute caisse qui n’a pas de disposition')}>
                        <span style={{
                          fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                          color: 'var(--teal-800)', background: 'var(--teal-100)', borderRadius: 'var(--r-xs)', padding: '2px 6px',
                        }}>{t('Par défaut')}</span>
                      </Tooltip>
                    )}
                  </span>
                  <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2 }}>
                    {destinationSubline(l)}
                  </span>
                </span>
                {/* Le gain RÉEL de cette destination — compté en base, jamais déduit du compte de tuiles. */}
                <span style={{
                  fontSize: 'var(--t-dense)', fontWeight: 700, flexShrink: 0, borderRadius: 'var(--r-pill)', padding: '4px 9px',
                  fontVariantNumeric: 'tabular-nums',
                  color: neuves > 0 ? 'var(--teal-800)' : 'var(--ink-500)',
                  background: neuves > 0 ? 'var(--teal-100)' : 'var(--ink-100)',
                }}>
                  {neuves > 0 ? `+${neuves}` : t('déjà tous')}
                </span>
              </button>
            );
          })}
          {failure && (
            <p role="alert" style={{ margin: 0, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--danger-ink)', lineHeight: 1.5 }}>
              {failure}
            </p>
          )}
        </div>

        {/* ⚠️ LE PIED DU DESSIN : renoncer à gauche, nu ; « Ajouter » en contour ; « Ajouter et organiser » en encre. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 20px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
          <button
            type="button" onClick={close}
            style={{ height: 36, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-600)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}
          >
            {t('Annuler')}
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button" onClick={() => void envoyer(false)} aria-disabled={off || undefined}
            style={{ ...FOOT_BUTTON, border: '1px solid var(--ink-300)', background: 'var(--paper)', color: 'var(--ink-800)', ...inert(off) }}
          >
            {writing ? t('Ajout…') : t('Ajouter')}
          </button>
          <button
            type="button" onClick={() => void envoyer(true)} aria-disabled={off || undefined}
            style={{ ...FOOT_BUTTON, border: 'none', background: 'var(--ink-900)', color: 'var(--paper)', ...inert(off) }}
          >
            {t('Ajouter et organiser')}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
